import React, { forwardRef, useMemo, useRef, useEffect, useState } from "react";

interface KioskSubstitutionTableProps {
  entries: any[];
  title: string;
  emptyMsg: string;
  displayKurzel: (v: string) => string;
}

function getRoleBadgeColor(roleName?: string) {
  if (!roleName) return "bg-slate-100 text-slate-700 border-slate-200";
  const r = roleName.toLowerCase();
  if (r.includes("erzieher"))
    return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (r.includes("teacher") || r.includes("lehrer"))
    return "bg-blue-100 text-blue-800 border-blue-200";
  if (r.includes("externe") || r.includes("kooperation"))
    return "bg-slate-100 text-slate-700 border-slate-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
}

// Group entries by class, keeping them together
function groupEntriesByClass(entries: any[]) {
  const groups: any[][] = [];
  let currentGroup: any[] = [];
  let currentClass = "";

  entries.forEach((entry, i) => {
    const entryClass = entry.klasse || "";

    if (i === 0 || entryClass === currentClass) {
      currentGroup.push(entry);
    } else {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = [entry];
    }
    currentClass = entryClass;
  });

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

// Distribute class groups across columns, trying to balance height
function distributeGroupsIntoColumns(
  groups: any[][],
  maxColumns: number,
  maxHeightPerColumn: number,
) {
  if (groups.length === 0) return [];

  // Calculate visual height of a group: each entry = 1 row, each bemerkung = +1 row,
  // plus 1 row for the class header/separator spacing
  const getGroupHeight = (group: any[]) => {
    let h = 1; // header/separator spacing
    for (const entry of group) {
      h += 1; // the entry row
      if (entry.bemerkung) h += 1; // note row
    }
    return h;
  };

  // Calculate total height
  const totalHeight = groups.reduce(
    (sum, group) => sum + getGroupHeight(group),
    0,
  );

  // Determine how many columns we actually need
  const neededColumns = Math.min(
    maxColumns,
    Math.ceil(totalHeight / maxHeightPerColumn),
  );

  if (neededColumns === 1) {
    return [groups];
  }

  // Calculate target height per column for better balance
  const targetHeightPerColumn = Math.ceil(totalHeight / neededColumns);

  const columns: any[][][] = [];
  let currentColumn: any[][] = [];
  let currentColumnHeight = 0;

  groups.forEach((group, index) => {
    const groupHeight = getGroupHeight(group);

    // Check if we should start a new column
    const wouldExceedTarget =
      currentColumnHeight + groupHeight > targetHeightPerColumn;
    const hasRoomForMore = columns.length < neededColumns - 1;
    const isNotLastGroup = index < groups.length - 1;

    if (
      wouldExceedTarget &&
      hasRoomForMore &&
      isNotLastGroup &&
      currentColumn.length > 0
    ) {
      // Start new column
      columns.push(currentColumn);
      currentColumn = [group];
      currentColumnHeight = groupHeight;
    } else {
      // Add to current column
      currentColumn.push(group);
      currentColumnHeight += groupHeight;
    }
  });

  // Add the last column
  if (currentColumn.length > 0) {
    columns.push(currentColumn);
  }

  return columns;
}

export const KioskSubstitutionTable = forwardRef<
  HTMLDivElement,
  KioskSubstitutionTableProps
>(({ entries, title, emptyMsg, displayKurzel }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(800);

  // Measure container height with ResizeObserver for accurate column distribution
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Guard: ResizeObserver may not be available on older kiosk browsers
    if (typeof ResizeObserver === "undefined") {
      if (el.clientHeight > 0) {
        setContainerHeight(el.clientHeight);
      }
      return;
    }

    const observer = new ResizeObserver((resizeEntries) => {
      for (const resizeEntry of resizeEntries) {
        const height = resizeEntry.contentRect.height;
        if (height > 0) {
          setContainerHeight(height);
        }
      }
    });

    observer.observe(el);
    // Also set initial height
    if (el.clientHeight > 0) {
      setContainerHeight(el.clientHeight);
    }

    return () => observer.disconnect();
  }, []);

  const columns = useMemo(() => {
    if (entries.length === 0) return [];

    const groups = groupEntriesByClass(entries);

    // Estimate rows per entry (header + content, roughly 40px per row)
    // Use 60% of container height as target to prefer multi-column layout
    const estimatedRowHeight = 40;
    const maxRowsPerColumn = Math.max(
      8,
      Math.floor((containerHeight * 0.6) / estimatedRowHeight),
    );

    // Distribute into up to 3 columns
    return distributeGroupsIntoColumns(groups, 3, maxRowsPerColumn);
  }, [entries, containerHeight]);

  const renderTable = (columnGroups: any[][], columnIndex: number) => {
    const flatEntries = columnGroups.flat();
    let groupIndex = 0;

    return (
      <div key={columnIndex} className="flex-1 min-w-0">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-100 border-b-2 border-slate-300 text-slate-900 uppercase tracking-wider kiosk-text-2xs font-black sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-2 py-1.5 w-[6%] rounded-tl-xl">Kl.</th>
              <th className="px-2 py-1.5 w-[6%]">Std.</th>
              <th className="px-2 py-1.5 w-[10%]">Fach</th>
              <th className="px-2 py-1.5 w-[28%]">Fachkraft</th>
              <th className="px-2 py-1.5 w-[25%]">Vertretung</th>
              <th className="px-2 py-1.5 w-[8%] rounded-tr-xl">Raum</th>
            </tr>
          </thead>
          <tbody className="kiosk-text-xs">
            {flatEntries.map((entry: any, i: number) => {
              const hasNoSub = !entry.vertretung;
              const hasRoomChange =
                !!entry.raum.new && entry.raum.new !== entry.raum.original;

              const currentClass = entry.klasse;
              const prevClass = i > 0 ? flatEntries[i - 1].klasse : null;
              const nextClass =
                i < flatEntries.length - 1 ? flatEntries[i + 1].klasse : null;

              // Increment group index if class changes
              if (i > 0 && currentClass !== prevClass) {
                groupIndex++;
              }

              // Determine background color
              const bgClass =
                groupIndex % 2 === 0 ? "bg-white" : "bg-slate-100";

              // Show class only if it's the first in the list or different from the previous one
              const showClass = i === 0 || currentClass !== prevClass;

              // Check if this is the last entry for this class group
              const isLastInGroup =
                i === flatEntries.length - 1 || currentClass !== nextClass;

              const rows = [];

              // Main row
              rows.push(
                <tr
                  key={`${columnIndex}-${i}`}
                  className={`${bgClass} hover:bg-slate-200 transition-colors ${
                    !entry.bemerkung && isLastInGroup
                      ? "border-b-2 border-slate-300"
                      : ""
                  }`}
                >
                  <td className="px-2 py-1 align-middle">
                    {showClass && entry.klasse && (
                      <span
                        className={`font-bold ${
                          entry.klasse === "Aufsicht"
                            ? "text-purple-700"
                            : "text-slate-900"
                        }`}
                      >
                        {entry.klasse}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1 align-middle">
                    <span className="inline-flex items-center justify-center px-1.5 h-5 rounded bg-slate-200 text-slate-900 font-black kiosk-text-xs whitespace-nowrap">
                      {entry.stunde.replace(". Stunde", "").substring(0, 4)}
                    </span>
                  </td>
                  <td className="px-2 py-1 align-middle">
                    <span
                      className={`font-mono kiosk-text-2xs bg-slate-100 text-slate-900 px-1 py-0.5 rounded border border-slate-200 font-medium whitespace-nowrap ${
                        entry.isCancelled
                          ? "line-through decoration-black decoration-[3px]"
                          : ""
                      }`}
                    >
                      {entry.fach}
                    </span>
                  </td>
                  <td className="px-2 py-1 align-middle overflow-hidden">
                    <div className="flex flex-wrap gap-1">
                      {entry.fehlendeKraft.map((t: any, j: number) => {
                        // Absent teachers → red strikethrough
                        // Not absent but lesson cancelled → black strikethrough (keep original color)
                        // Otherwise → normal color badge
                        if (t.isAbsent) {
                          return (
                            <span
                              key={j}
                              className="font-mono kiosk-text-2xs px-1 py-0.5 rounded font-medium whitespace-nowrap border text-red-700 bg-red-50 border-red-100 line-through decoration-red-500 decoration-2"
                            >
                              {displayKurzel(t.name)}
                            </span>
                          );
                        } else if (entry.isCancelled) {
                          return (
                            <span
                              key={j}
                              className={`font-mono kiosk-text-2xs px-1 py-0.5 rounded font-medium whitespace-nowrap border line-through decoration-black decoration-[3px] ${getRoleBadgeColor(
                                t.role,
                              )}`}
                            >
                              {displayKurzel(t.name)}
                            </span>
                          );
                        } else {
                          return (
                            <span
                              key={j}
                              className={`font-mono kiosk-text-2xs px-1 py-0.5 rounded font-medium whitespace-nowrap border ${getRoleBadgeColor(
                                t.role,
                              )}`}
                            >
                              {displayKurzel(t.name)}
                            </span>
                          );
                        }
                      })}
                    </div>
                  </td>
                  <td className="px-2 py-1 align-middle overflow-hidden">
                    {entry.isCancelled ? (
                      entry.isCourse && entry.klasse && !(entry.klasse || '').toLowerCase().includes('flex') ? (
                        <span className="text-purple-700 font-medium kiosk-text-2xs">Teilung entfällt</span>
                      ) : (
                        <span className="text-red-700 font-black">entfällt</span>
                      )
                    ) : hasNoSub ? (
                      <span className="text-slate-400 font-bold">—</span>
                    ) : (
                      <div className="flex flex-wrap items-center gap-1">
                        {entry.vertretung!.map(
                          (sub: { name: string; role: string }, k: number) => {
                            const isInternal = (entry.internalSubNames || []).some(
                              (isn: string) => isn.toLowerCase() === sub.name.toLowerCase(),
                            );
                            return (
                              <span key={k} className="flex items-center gap-0.5">
                                {isInternal && (
                                  <span className="text-green-700 font-bold kiosk-text-2xs">→</span>
                                )}
                                <span
                                  className={`font-mono kiosk-text-2xs px-1 py-0.5 rounded border font-bold whitespace-nowrap ${getRoleBadgeColor(
                                    sub.role,
                                  )}`}
                                >
                                  {displayKurzel(sub.name)}
                                </span>
                              </span>
                            );
                          },
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1 align-middle">
                    {hasRoomChange ? (
                      <div className="flex items-center gap-1">
                        <span className="line-through text-slate-400 kiosk-text-2xs decoration-2">
                          {entry.raum.original}
                        </span>
                        <span className="text-amber-700 font-black">
                          → {entry.raum.new}
                        </span>
                      </div>
                    ) : entry.isCancelled ? (
                      <span className="text-slate-700 font-medium line-through decoration-black decoration-[3px]">
                        {entry.raum.original}
                      </span>
                    ) : (
                      <span className="text-slate-700 font-medium">
                        {entry.raum.original}
                      </span>
                    )}
                  </td>
                </tr>,
              );

              // Note row (if exists)
              if (entry.bemerkung) {
                rows.push(
                  <tr
                    key={`${columnIndex}-${i}-note`}
                    className={`${bgClass} ${
                      isLastInGroup ? "border-b-2 border-slate-300" : ""
                    }`}
                  >
                    <td className="px-2 py-0.5"></td>
                    <td colSpan={5} className="px-2 py-0.5">
                      <div className="flex items-center gap-1">
                        <span className="text-yellow-500 kiosk-text-2xs flex-shrink-0">
                          ⚠
                        </span>
                        <div className="kiosk-text-2xs text-blue-700 font-medium leading-tight">
                          {entry.bemerkung}
                        </div>
                      </div>
                    </td>
                  </tr>,
                );
              }

              return rows;
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div
      ref={ref}
      className="bg-white border-2 border-slate-200 shadow-sm rounded-xl flex flex-col h-full"
    >
      <div ref={containerRef} className="overflow-hidden flex-1">
        {entries.length === 0 ? (
          <div className="px-4 py-8 text-center text-slate-400 font-medium italic">
            {emptyMsg}
          </div>
        ) : (
          <div className="flex gap-4 h-full">
            {columns.map((columnGroups, idx) => renderTable(columnGroups, idx))}
          </div>
        )}
      </div>
    </div>
  );
});

KioskSubstitutionTable.displayName = "KioskSubstitutionTable";
